import { ViewModeCapabilityCoordinator } from '@mobile-mapping/web3d';
import {
  StationNavigation,
  TrbToolbar,
  groupHoverReveal,
  type StationNavigationSize,
} from '@trimble/ui-components/solidjs';
import { PopupButton } from '@trimble/pane-layout';
import { createElementWidth } from '@trimble/solidjs-utils';
import { Accessor, Component, createMemo, createSignal, Show } from 'solid-js';
import { ViewModePicker } from '../view-mode-picker/ViewModePicker';
import type { ViewToolsDropdownProps } from '../view-tools-dropdown/ViewToolsDropdown';

const BOTTOM_BAR_LARGE_PX = 500;
const BOTTOM_BAR_MEDIUM_PX = 320;

interface PaneBottomBarProps {
  readonly viewModeCoordinator: Accessor<
    ViewModeCapabilityCoordinator | undefined
  >;
  readonly viewToolActions?: ViewToolsDropdownProps['actions'];
  readonly isStationNavVisible: Accessor<boolean>;
  readonly showStationNavClose: Accessor<boolean>;
  readonly allowPopup?: boolean;
}

/**
 * Bottom bar for a pane. View-mode picker anchors bottom-left, station-nav
 * (when visible) sits on the right pushed by the flex-1 spacer, and the
 * popup button — when present — trails after it in the far-right corner.
 * Right-aligning station-nav avoids the pane-centre overlap with the
 * view-mode picker in narrow panes.
 *
 * The row's own width (pane-driven via `inset-x-3`, independent of its
 * children) drives the responsive {@link StationNavigationSize} tiers so the
 * station-nav controls compress as the pane shrinks. Measuring the row rather
 * than the station-nav itself avoids a resize feedback loop.
 */
export const PaneBottomBar: Component<PaneBottomBarProps> = (props) => {
  const [bottomBarEl, setBottomBarEl] = createSignal<HTMLDivElement>();
  const bottomBarWidth = createElementWidth(bottomBarEl);

  const stationNavSize = createMemo((): StationNavigationSize => {
    const w = bottomBarWidth();
    if (w >= BOTTOM_BAR_LARGE_PX) return 'large';
    if (w >= BOTTOM_BAR_MEDIUM_PX) return 'medium';
    return 'small';
  });

  return (
    <div
      ref={setBottomBarEl}
      class={`absolute inset-x-3 bottom-3 z-toolbar pointer-events-none ${groupHoverReveal}`}
      data-testid="pane-bottom-toolbar-row"
    >
      <div class="flex items-end gap-3">
        <TrbToolbar
          orientation="horizontal"
          customClasses="flex-shrink-0 pointer-events-auto hover:z-dropdown focus-within:z-dropdown"
        >
          <Show when={props.viewModeCoordinator()}>
            <ViewModePicker
              coordinator={props.viewModeCoordinator()!}
              viewToolActions={props.viewToolActions}
            />
          </Show>
        </TrbToolbar>
        <div class="flex-1" />
        <Show when={props.isStationNavVisible()}>
          <TrbToolbar
            orientation="horizontal"
            addSeparators={false}
            customClasses="flex-shrink-0 items-center pointer-events-auto hover:z-dropdown focus-within:z-dropdown"
          >
            <StationNavigation
              stationNavigationCapability={() =>
                props.viewModeCoordinator()?.stationNavigationController()
              }
              showCloseButton={props.showStationNavClose()}
              size={stationNavSize()}
            />
          </TrbToolbar>
        </Show>
        <Show when={props.allowPopup}>
          <TrbToolbar
            orientation="horizontal"
            customClasses="flex-shrink-0 pointer-events-auto hover:z-dropdown focus-within:z-dropdown"
          >
            <PopupButton />
          </TrbToolbar>
        </Show>
      </div>
    </div>
  );
};
